import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentSwitcher, DataTableSkeleton, Pagination, Switch } from '@carbon/react';
import { ColumnManager } from './ColumnManager';
import { CrudBatchBar } from './CrudBatchBar';
import { CrudDeleteModal } from './CrudDeleteModal';
import { CrudFilterBar } from './CrudFilterBar';
import { CrudFormModal, MODAL_EXIT_MS } from './CrudFormModal';
import { CrudShell } from './CrudShell';
import { CrudTable } from './CrudTable';
import { CrudRowAction } from './CrudTable';
import { CrudToolbar } from './CrudToolbar';
import { CrudView, CrudViewContext } from './crudViews';
import { toCsvColumns } from './csvColumns';
import { CrudPageConfig } from './definePage';
import { CrudBulkAction, Paginated } from './defineResource';
import { buildCsv, csvFilename, downloadCsv } from './exportCsv';
import { TenantParams, useCrudConfig } from './provider';
import { useColumnPrefs } from './useColumnPrefs';
import { UseCrudResourceResult, useCrudResource } from './useCrudResource';
import { referenceOptionsKey } from './useReferenceOptions';
import { useRowSelection } from './useRowSelection';
import { useViewPref } from './useViewPref';

export type CrudPageProps<TEntity, TCreate, TUpdate, TScope extends TenantParams, TValues> = CrudPageConfig<
  TEntity,
  TCreate,
  TUpdate,
  TScope,
  TValues
> & {
  /** Slot: page-specific filters, rendered between search and the create button. */
  toolbar?: (state: UseCrudResourceResult<TEntity, TCreate, TUpdate>) => React.ReactNode;
  /**
   * Page-specific row actions, rendered between Edit and Delete so Delete keeps its
   * last, danger-marked position (e.g. InventoryItems' "Record movement").
   */
  /**
   * Whether the caller may create, edit or delete. Default true.
   *
   * False hides the create button and passes an EMPTY actions array to CrudTable — including
   * `extraActions`, because a caller who may not write has no business reaching a page's
   * custom write action either. CrudTable already renders no actions column for an empty
   * array, so nothing else has to change.
   */
  canWrite?: boolean;
  /**
   * Narrower gates for pages where the three writes do not travel together. Each defaults to
   * `canWrite`, so passing neither keeps today's all-or-nothing behaviour and passing only
   * `canWrite` still turns everything off at once.
   *
   * Organization Members needs this: a member row can have its role changed and be removed,
   * but members are not created here — an invitation is, and it lands on another page.
   * Invitations is the mirror image: created and revoked, never edited.
   */
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  /**
   * Per-row gates for the built-in edit and delete actions. `canUpdate`/`canDelete` answer
   * "may this user write at all"; these answer "may this particular row take that action" —
   * the last owner of an organization cannot be demoted or removed however privileged the
   * caller is. Return true to allow. Omit them and every row is writable, which is what
   * every existing page gets.
   *
   * A page's own `extraActions` carry the same gate as `disabled` on the action itself.
   */
  canUpdateRow?: (row: TEntity) => boolean;
  canDeleteRow?: (row: TEntity) => boolean;
  /**
   * Alternative renderings of the same resource. Omit it and the page renders the table with
   * no switcher, exactly as it did before this prop existed.
   *
   * The active view is remembered per resource key, so sibling routes over one resource (the
   * four Issues tabs) share the choice — the same thing column preferences already do.
   */
  views?: CrudView<TEntity, TCreate, TUpdate>[];
  extraActions?: CrudRowAction<TEntity>[];
  /**
   * Actions offered while rows are selected. Omit and the table renders no
   * checkboxes. A delete action needs `canDelete`, any other `canWrite`; with none
   * left after those gates the table renders no checkboxes either.
   */
  bulkActions?: CrudBulkAction<TEntity>[];
  /**
   * Replaces the built-in create form: the toolbar's create button (same label, place and
   * gating) calls this instead — for a page whose "create" is a different flow, like
   * Members, where inviting produces an Invitation rather than a row here.
   */
  onCreate?: () => void;
};

export function CrudPage<TEntity extends { id: string }, TCreate, TUpdate, TScope extends TenantParams, TValues>(
  props: CrudPageProps<TEntity, TCreate, TUpdate, TScope, TValues>
) {
  const { presentation, resource } = props;
  const canWrite = props.canWrite ?? true;
  const canCreate = props.canCreate ?? canWrite;
  const canUpdate = props.canUpdate ?? canWrite;
  const canDelete = props.canDelete ?? canWrite;
  const { canUpdateRow, canDeleteRow } = props;
  const { translate, resolveErrorMessage, useTenantParams } = useCrudConfig();
  // Stored preferences are per tenant (prefsStorage.ts), not just per resource.
  const tenantParams = useTenantParams();
  const prefsScope = { orgSlug: tenantParams.orgSlug, restaurantId: tenantParams.restaurantId };
  // The whitelist mirrors the columns: whatever the config marks sortable is what the server
  // accepts, so a hand-edited URL degrades to the default order instead of a 400.
  const sortableKeys = useMemo(
    () => presentation.columns.filter((column) => column.sortable).map((column) => column.key),
    [presentation.columns]
  );
  const views = props.views ?? [];
  const viewKeys = views.length > 0 ? views.map((view) => view.key) : ['table'];
  const [activeViewKey, setActiveViewKey] = useViewPref(resource.key, viewKeys, prefsScope);
  const activeView = views.find((view) => view.key === activeViewKey);
  const isTableView = !activeView?.render;

  const state = useCrudResource(resource, { sortableKeys, enabled: isTableView });
  const formRef = useRef<any>();

  // The chip bar renders whenever the resource declares filterable fields; the chips
  // themselves come from the URL-driven state the resource hook manages.
  // State, not a ref: the slot node has to trigger a re-render once it exists, or the
  // portal would have nowhere to go on the first paint.
  const queryClient = useQueryClient();
  const [filterAddSlot, setFilterAddSlot] = useState<HTMLElement | null>(null);
  const filterBar = resource.filterable?.length ? (
    <CrudFilterBar
      filterable={resource.filterable}
      filters={state.filters}
      onChange={state.setFilters}
      addSlot={filterAddSlot}
      views={{
        resourceKey: resource.key,
        urlState: { page: state.page, search: state.search, sort: state.sort, filters: state.filters },
        // A saved view never carries a page (R15): applying one writes its search/sort/filters
        // and pins page 1, in the single write `patch` guarantees.
        onApply: (view) => state.patch({ ...view, page: 1 }),
      }}
    />
  ) : null;

  // CrudPage owns the column preferences: it is the only component that knows both
  // resource.key (the storage key) and the column config. CrudTable receives the resolved
  // columns; CrudToolbar receives the manager node.
  const [prefs, setPrefs] = useColumnPrefs(resource.key, presentation.columns, prefsScope);
  const { columns } = presentation;
  const orderedColumns = useMemo(
    () =>
      prefs.order
        .map((key) => columns.find((column) => column.key === key))
        .filter((column): column is (typeof columns)[number] => Boolean(column)),
    [prefs.order, columns]
  );
  const visibleColumns = orderedColumns.filter((column) => !prefs.hidden.includes(column.key));
  const columnManager = (
    <ColumnManager
      columns={orderedColumns.map((column) => ({
        key: column.key,
        label: translate(column.headerKey),
        alwaysVisible: column.alwaysVisible,
      }))}
      prefs={prefs}
      onChange={setPrefs}
      sort={state.sort}
      onSortChange={state.setSort}
    />
  );

  const cleanupTimer = useRef<number>();
  // The cleanup a close deferred, if its timer has not fired yet. See closeForm and openForm.
  const pendingCleanup = useRef<(() => void) | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TEntity | null>(null);
  const [deleting, setDeleting] = useState<TEntity | null>(null);
  // A refusal raised by the page's OWN toPayload — as opposed to one the server sent back.
  // Kept apart from state.saveError because it must not be cleared by a mutation reset.
  const [formError, setFormError] = useState<string | null>(null);

  const titleVars = presentation.useTitleVars();
  const title = translate(presentation.titleKey, titleVars);

  const closeForm = useCallback(() => {
    // Closing does one thing: start the animation. Carbon never unmounts a Modal — it drops
    // `is-visible` and fades it out over MODAL_EXIT_MS with the children still mounted and on
    // screen. Clearing the save error unmounts its notification and resetting the fields can
    // unmount conditional ones, so doing either here changed the body's height mid-fade, which
    // is what read as a flicker. The cleanup is still required — a second Create after a first
    // one reopens with the same `initialValues`, so `enableReinitialize` alone would leave the
    // previous values in place — it just has to happen once the modal is actually gone.
    setCreating(false);
    setEditing(null);
    window.clearTimeout(cleanupTimer.current);
    pendingCleanup.current = () => {
      pendingCleanup.current = null;
      state.create.reset();
      setFormError(null);
      state.update.reset();
      formRef.current?.resetForm();
    };
    cleanupTimer.current = window.setTimeout(() => pendingCleanup.current?.(), MODAL_EXIT_MS);
  }, [state.create, state.update]);

  /**
   * Opening runs a cleanup the previous close is still waiting on, NOW. Left to its timer it would
   * fire into the session being opened — a quick Cancel then Create again — and wipe what the user
   * has started typing. Content may change freely here: the modal is appearing, not fading.
   */
  const openForm = useCallback((row: TEntity | null) => {
    window.clearTimeout(cleanupTimer.current);
    pendingCleanup.current?.();
    if (row) {
      setEditing(row);
    } else {
      setCreating(true);
    }
  }, []);

  // Unmounting mid-fade would otherwise leave the timer to fire against a dead tree.
  useEffect(() => () => window.clearTimeout(cleanupTimer.current), []);

  // Each delete should start clean: without this, refusing to delete row A, cancelling, then
  // opening Delete on row B shows B's modal already carrying A's leftover error.
  //
  // Deliberately depends on `deleting` alone: react-query's mutation result (`state.remove`)
  // is a fresh object every render, not memoized, so including it here would rerun this
  // effect - and therefore reset() the error right back out - on every re-render while the
  // modal is open, which is exactly the failure the effect exists to prevent.
  useEffect(() => {
    if (deleting) {
      state.remove.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleting]);

  const handleSubmit = useCallback(
    async (values: TValues) => {
      // `any`, matching toPayload's own declared return type: the payload is deliberately
      // untyped at this seam (see CrudPresentation.toPayload), so no cast gymnastics are added.
      let payload: any;
      try {
        payload = presentation.toPayload(values, state.params);
      } catch (error) {
        // Rendered through the modal's error slot. The resolver gets first say; a page that
        // throws a plain Error with no server payload behind it still gets its reason shown.
        setFormError(resolveErrorMessage(error) ?? (error instanceof Error ? error.message : String(error)));
        return;
      }
      if (editing) {
        await state.update.mutateAsync({ ...payload, id: editing.id });
      } else {
        await state.create.mutateAsync(payload);
      }
      closeForm();
    },
    [editing, state.params, state.update, state.create, closeForm, presentation, resolveErrorMessage]
  );

  const viewSwitcher =
    views.length > 1 ? (
      <ContentSwitcher
        selectedIndex={viewKeys.indexOf(activeViewKey)}
        onChange={({ index }: { index: number }) => setActiveViewKey(viewKeys[index])}
        size="sm"
        className="crud-view-switcher"
      >
        {/* No `text`: Carbon's Switch renders its children instead, which is how the icon fits. */}
        {views.map((view) => {
          const Icon = view.icon;
          return (
            <Switch key={view.key} name={view.key}>
              {Icon && <Icon size={16} />}
              {translate(view.labelKey)}
            </Switch>
          );
        })}
      </ContentSwitcher>
    ) : null;

  /**
   * Exports what the table is SHOWING: the visible columns in their current order, and the
   * rows the active filters, search and sort produced. An export that ignored the narrowing
   * the user just built would be a different, much less useful feature.
   *
   * Built at click time rather than on every render — nothing here is needed until then,
   * and reading the reference cache lazily avoids calling `useReferenceOptions` once per
   * reference column and depending on their order staying fixed.
   */
  const exportCurrentView = useCallback(() => {
    const csvColumns = toCsvColumns(visibleColumns, {
      translate,
      lookupReference: (column, id) => {
        const display = column.display;
        if (!display || display.kind !== 'reference') {
          return null;
        }
        const cached = queryClient.getQueryData<Paginated<unknown>>(referenceOptionsKey(display.load, state.params));
        const option = (cached?.data ?? []).find((entry) => String((entry as { id?: unknown })?.id) === id);
        return option ? display.optionLabel(option) : null;
      },
    });
    downloadCsv(csvFilename(resource.key), buildCsv(csvColumns, state.rows));
  }, [visibleColumns, translate, queryClient, state.params, state.rows, resource.key]);

  // Any change to what the page is showing clears the selection.
  const selectionResetKey = JSON.stringify({
    page: state.page,
    search: state.search,
    sort: state.sort,
    filters: state.filters,
  });
  const selection = useRowSelection(state.rows, selectionResetKey);
  const [bulkRunning, setBulkRunning] = useState(false);
  // A pending bulk-delete confirmation. `proceed` runs the action through the batch bar, so
  // a failure still surfaces there. Held in an object: a bare function passed to a state
  // setter would be called as an updater.
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ proceed: () => Promise<void> } | null>(null);
  // It belongs to the selection it asked about. If that selection is reset under it (a
  // Back navigation changes the page), confirming must not delete rows no longer on screen.
  if (bulkDeleteConfirm && !selection.someSelected) {
    setBulkDeleteConfirm(null);
  }

  const bulkActions = useMemo(
    () =>
      (props.bulkActions ?? [])
        // The same page-level gates as the row actions: a delete answers to `canDelete`,
        // anything else to `canWrite` — so a caller who may delete but not edit keeps bulk
        // delete, and `canDelete === false` removes it (spec §2).
        .filter((action) => (action.isDelete ? canDelete : canWrite))
        .map((action) => ({
          ...action,
          // A per-row gate binds the bulk path too: a selection containing one
          // protected row must not offer the action that row refuses.
          enabledFor: (rows: TEntity[]) => {
            if (action.isDelete && canDeleteRow && !rows.every((row) => canDeleteRow(row))) {
              return false;
            }
            if (!action.isDelete && canUpdateRow && !rows.every((row) => canUpdateRow(row))) {
              return false;
            }
            return action.enabledFor ? action.enabledFor(rows) : true;
          },
        })),
    [canWrite, canDelete, props.bulkActions, canDeleteRow, canUpdateRow]
  );

  const runBulk = useCallback(
    async (action: CrudBulkAction<TEntity>, rows: TEntity[]) => {
      setBulkRunning(true);
      try {
        await action.run(rows);
        selection.clear();
      } finally {
        // Also on failure: an action that fails part-way has already changed some rows.
        state.refetch();
        setBulkRunning(false);
      }
    },
    [state, selection]
  );

  if (isTableView && state.isLoading) {
    return <DataTableSkeleton columnCount={visibleColumns.length + (canWrite ? 1 : 0)} rowCount={6} />;
  }

  const FormComponent = presentation.form;

  return (
    <>
      <CrudShell
        title={presentation.hideTitle ? undefined : title}
        description={
          isTableView
            ? `${state.total} ${translate(presentation.descriptionKey)}`
            : translate(presentation.descriptionKey)
        }
      >
        <CrudToolbar
          search={state.search}
          onSearchChange={state.setSearch}
          createLabel={translate(presentation.createLabelKey, titleVars)}
          onCreate={props.onCreate ?? (() => openForm(null))}
          showCreate={canCreate}
          filterBar={filterBar}
          onExport={resource.exportable === false ? undefined : exportCurrentView}
          exportRowCount={state.rows.length}
          onFilterAddSlot={resource.filterable?.length ? setFilterAddSlot : undefined}
          canClear={state.search !== '' || state.sort.length > 0 || Object.keys(state.filters).length > 0}
          onClear={state.clear}
          viewSwitcher={viewSwitcher}
          columnManager={isTableView ? columnManager : undefined}
          batchBar={
            selection.someSelected && bulkActions.length > 0 ? (
              <CrudBatchBar<TEntity>
                selectedRows={selection.selectedRows}
                actions={bulkActions.map((action) => ({
                  ...action,
                  run: (rows: TEntity[]) => runBulk(action, rows),
                }))}
                onCancel={selection.clear}
                running={bulkRunning}
                confirmDelete={(proceed) => setBulkDeleteConfirm({ proceed })}
              />
            ) : undefined
          }
        >
          {props.toolbar?.(state)}
        </CrudToolbar>
        {isTableView ? (
          <CrudTable<TEntity>
            rows={state.rows}
            columns={visibleColumns}
            emptyState={presentation.emptyState}
            sort={state.sort}
            onSortChange={state.setSort}
            density={prefs.density}
            selection={bulkActions.length > 0 ? selection : undefined}
            // Assembled per action rather than as one block, so a page that may edit but
            // not delete (or the reverse) still gets the actions it is entitled to. An
            // empty array renders no actions column at all, which is what a read-only
            // caller saw before these narrower gates existed.
            actions={[
              ...(canUpdate
                ? [
                    {
                      labelKey: 'actions.edit',
                      onClick: (row: TEntity) => openForm(row),
                      disabled: canUpdateRow ? (row: TEntity) => !canUpdateRow(row) : undefined,
                    },
                  ]
                : []),
              ...(canWrite ? props.extraActions ?? [] : []),
              ...(canDelete
                ? [
                    {
                      labelKey: 'actions.delete',
                      isDelete: true,
                      onClick: (row: TEntity) => setDeleting(row),
                      disabled: canDeleteRow ? (row: TEntity) => !canDeleteRow(row) : undefined,
                    },
                  ]
                : []),
            ]}
          />
        ) : (
          activeView?.render?.({
            state,
            compiledQuery: state.compiledQuery,
            params: state.params,
            canWrite,
            onEdit: openForm,
            onDelete: setDeleting,
          } as CrudViewContext<TEntity, TCreate, TUpdate>)
        )}
        {/*
          Always rendered on the table view. It was conditional on there being more than one
          page, which made the bar appear and vanish as a filter narrowed the list and left
          the reader with no fixed place to see the total or change the page size. The bar
          is also the only way to reach rows past the first page — without it a list
          silently truncates at pageSize and a newly created row (sorted createdAt ASC)
          lands on an unreachable page, looking like it was never saved.
        */}
        {isTableView && (
          <Pagination
            backwardText={translate('common.previousPage')}
            forwardText={translate('common.nextPage')}
            itemsPerPageText={`${translate('common.itemsPerPage')}:`}
            // itemRangeText, not itemText: Carbon only calls itemText when `pagesUnknown` is
            // set, which this bar never sets, so translating that prop left the range text
            // rendering Carbon's untranslated English default ("1–20 of 34 items").
            // One whole-sentence key rather than common.of + common.items stitched together:
            // common.of is capitalised for the start of pageRangeText ("Of 3 Pages"), which
            // made this read "1–20 Of 34 items" mid-sentence.
            itemRangeText={(min: number, max: number, total: number) =>
              translate('common.itemRange', { min, max, total })
            }
            pageRangeText={(_current: number, total: number) =>
              `${translate('common.of')} ${total} ${total === 1 ? translate('common.page') : translate('common.pages')}`
            }
            page={state.page}
            pageSize={state.pageSize}
            // Always include the current size, or Carbon warns when a resource overrides it.
            pageSizes={Array.from(new Set([state.pageSize, 20, 50, 100])).sort((a, b) => a - b)}
            size="lg"
            totalItems={state.total}
            onChange={({ page, pageSize }: { page: number; pageSize: number }) => {
              state.setPage(page);
              state.setPageSize(pageSize);
            }}
          />
        )}
      </CrudShell>

      <CrudFormModal
        open={creating || !!editing}
        editing={!!editing}
        // The title vars ride along so a page whose copy is composed per route (the ledger
        // pages' "Create Transaction" / "Create Income") can interpolate {{title}} without
        // one key per variant. Keys without placeholders ignore the vars.
        heading={translate(editing ? presentation.updateLabelKey : presentation.createLabelKey, titleVars)}
        label={title}
        submitting={state.submitting}
        errorMessage={state.saveError ?? formError}
        formRef={formRef}
        onClose={closeForm}
      >
        {/*
          Keyed on the row so create and edit are different mounts. CrudPage resets the form
          imperatively BEFORE the re-render that passes initialValues={null}, so a form that
          simply forwards Formik's handle would reset to the row just edited and pre-fill the
          next create with it. Keying makes the fresh start structural rather than a convention
          every form component has to remember.
        */}
        <FormComponent key={editing?.id ?? 'create'} ref={formRef} initialValues={editing} onSubmit={handleSubmit} />
      </CrudFormModal>

      <CrudDeleteModal
        open={!!deleting}
        heading={translate(presentation.deleteHeadingKey, titleVars)}
        label={title}
        deleting={state.remove.isLoading}
        errorMessage={state.deleteError}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          // The modal only opens for a row, so deleting is set whenever its confirm button
          // can be clicked; the ternary replaces what used to be a non-null assertion. The
          // promise MUST still be returned — CrudDeleteModal awaits it to swallow a refused
          // delete, and discarding it turns that refusal into an unhandled rejection.
          return deleting ? state.remove.mutateAsync(deleting.id) : Promise.resolve();
        }}
      />

      {/*
        The bulk delete's confirmation. `proceed` never rejects (the batch bar catches and
        shows the failure), so the modal simply closes once the run settles; `errorMessage`
        stays null because the error has its own place in the bar.
      */}
      <CrudDeleteModal
        open={!!bulkDeleteConfirm}
        heading={translate('messages.confirmDeleteSelected', { count: selection.selectedRows.length })}
        label={title}
        deleting={bulkRunning}
        errorMessage={null}
        onClose={() => setBulkDeleteConfirm(null)}
        onConfirm={() => (bulkDeleteConfirm ? bulkDeleteConfirm.proceed() : Promise.resolve())}
      />
    </>
  );
}
